import type { ReactElement } from "react";
import type { ElementProps } from "../functionalComponents/RenderPage";

export interface GridObject {
  col: number;
  row: number;
  maxCols: number;
  lastColInRow: boolean;
  maxRows: number;
  isSplitCell: boolean;
  lastRowInGrid: boolean;
  elArray: ReactElement<ElementProps>[];
  isResponsiveTable?: boolean;
  columnTitleAtomId?: number;
  isSpacer?: boolean;
  colSpan?: number;
  /** true when Tufte media weighting was applied to this grid */
  hasWeightedRows?: boolean;
}

/** vertical breathing room for Tufte-constrained layouts (weighted grids and
 * stacked pages), as a fraction of slide height — same rhythm as --slide-gap */
export const TUFTE_VERTICAL_PAD_FRACTION = 0.05;

export interface ContentContext {
  baseFontSize: number;
  /** words per text element, keyed by atomId */
  wordCounts: Map<number, number>;
  /** natural aspect ratios (width/height) of loaded media, keyed by atomId */
  mediaAspects?: Record<number, number>;
  slideWidth?: number;
  slideHeight?: number;
}

const MEDIA_TYPES = ["image:", "video:", "chart:"] as const;

export function isMediaType(type: string): boolean {
  return MEDIA_TYPES.includes(type as typeof MEDIA_TYPES[number]);
}

const TEXT_TYPES = ["paragraph:", "title:", "subtitle:"] as const;

export function isTextType(type: string): boolean {
  return TEXT_TYPES.includes(type as typeof TEXT_TYPES[number]);
}

export function countWords(content: unknown): number {
  if (typeof content !== "string") return 0;
  return content.split(/\s+/).filter(Boolean).length;
}

/**
 * Compute the percentage width for media elements based on text density and font size.
 * More text → smaller media. Larger font → effectively denser text (fewer words fit).
 * Continuous ramp from 75% (no text) down to 50% (60+ font-adjusted words).
 */
export function computeMediaWeight(totalWords: number, baseFontSize: number): number {
  // Scale thresholds by font size: larger fonts mean words take more space,
  // so fewer words count as "dense"
  const fontScale = Math.max(baseFontSize / 10, 0.5); // 10 is typical baseFontSize
  const adjusted = totalWords / fontScale;
  return Math.round(Math.min(75, Math.max(50, 75 - (adjusted * 25) / 60)));
}
